import type React from "react";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { normalizePathname } from "@helpers/utils";

export interface UseRowNavigationOptions {
  /**
   * Optional navigation state to pass when navigating.
   * Can be a static object or a function that receives the row.
   */
  navigationState?: Record<string, any> | ((row: any) => Record<string, any>);
}

/**
 * Hook for handling row click navigation with support for opening in new tab
 * via Ctrl/Cmd+click, middle mouse button, or Ctrl/Cmd+Enter keyboard.
 *
 * @param getPath - Function that takes a row and returns the navigation path
 * @param options - Optional configuration
 * @returns handleRowClick callback
 */
export function useRowNavigation<T extends { id: string }>(
  getPath: (row: T) => string,
  options: UseRowNavigationOptions = {},
) {
  const { navigationState } = options;
  const basename = normalizePathname(useLocation().pathname);
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (row: T, event?: React.MouseEvent<Element> | React.KeyboardEvent) => {
      const rawPath = getPath(row);
      const path = rawPath.startsWith("/") ? rawPath : `${basename}/${rawPath}`;

      // Open in new tab on Ctrl/Cmd+click, middle mouse button (button 1), or Ctrl/Cmd+Enter
      const isMiddleClick =
        "button" in (event ?? {}) && (event as React.MouseEvent).button === 1;
      if (event && (event.ctrlKey || event.metaKey || isMiddleClick)) {
        const url = `${window.location.origin}${path}`;
        window.open(url, "_blank", "noopener");
      } else {
        const state =
          typeof navigationState === "function"
            ? navigationState(row)
            : navigationState;
        navigate(path, state ? { state } : undefined);
      }
    },
    [navigate, basename, getPath, navigationState],
  );

  return handleRowClick;
}
